import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { SiteDto, ClientDto, ProjectDto, SiteType } from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';
import { LocationInfo } from '../../common/Extensions';
import { addClientToRedux } from './clientActions';

export const createNewEmptyCustomer = createAction('site/createNewEmptyCustomer');
export const clearSite = createAction('site/clearSite');

export const updateClientAddress = createAction<string>('site/updateClientAddress');
export const updateClientCity = createAction<string>('site/updateClientCity');
export const updateClientState = createAction<string>('site/updateClientState');
export const updateClientClientType = createAction<string>('site/updateClientClientType');
export const updateClientContactInfo = createAction<string>('site/updateClientContactInfo');
export const updateClientEmail = createAction<string>('site/updateClientEmail');
export const updateClientEntityType = createAction<string>('site/updateClientEntityType');
export const updateClientName = createAction<string>('site/updateClientName');
export const updateClientShortName = createAction<string>('site/updateClientShortName');
export const updateClientTaxId = createAction<string>('site/updateClientTaxId');
export const updateClientPhone = createAction<string>('site/updateClientPhone');
export const updateClientUcin = createAction<string>('site/updateClientUcin');
export const selectClient = createAction<number | undefined>('site/selectClient');
export const toggleCreateNewClient = createAction<void>('site/toggleCreateNewClient');

export const createNewEmptyProject = createAction('site/createNewEmptyProject');
export const updateProjectName = createAction<string>('site/updateProjectName');
export const toggleCreateNewProject = createAction<void>('site/toggleCreateNewProject');
export const selectProject = createAction<number>('site/selectProject');

export const createNewEmptySite = createAction('site/createNewEmptySite');
export const createNewEmptyWarehouse = createAction('site/createNewEmptyWarehouse');
export const createNewEmptyConstructionSite = createAction('site/createNewEmptyConstructionSite');
export const updateSiteName = createAction<string>('site/updateSiteName');
export const updateSiteLocationInfo = createAction<LocationInfo>('site/updateSiteLocationInfo');
export const updateSiteSiteType = createAction<string>('site/updateSiteSiteType');

export const updateReduxSite = createAsyncThunk<SiteDto, number>('site/updateReduxSite', async (payload, thunkAPI) => {
    const destSiteId = payload;
    return WarehouseClient.getSiteById(destSiteId, true, true);
});

export const saveSite = createAsyncThunk<SiteDto, void>('site/saveSite', async (payload, thunkAPI) => {
    const rootState = thunkAPI.getState() as RootState;

    const site = rootState.site.site;
    const siteId = rootState.site.oldSiteId;
    const client = rootState.site.client;
    const project = rootState.site.project;
    const clientId = rootState.site.newClientId;
    const projectId = rootState.site.newProjectId;
    const isNewClient = rootState.site.createNewClient;
    const isNewProject = rootState.site.createNewProject;

    myAssert(() => site !== null);

    if (site === null) {
        throw new Error();
    }

    let savedSite: SiteDto | undefined;

    if (!!siteId && siteId !== 0) {
        const tempSite = new SiteDto(site);

        myAssert(() => tempSite.id !== undefined);

        if (tempSite.id === undefined) {
            throw new Error();
        }

        const sitePromise = WarehouseClient.updateSite(tempSite.id, tempSite as SiteDto);

        const allPromise = Promise.all([sitePromise]);

        await allPromise;

        savedSite = await sitePromise;
    } else {
        if ((!clientId || clientId === 0) && isNewClient) {
            if (!siteId || siteId === 0) {
                const addSitePromise = WarehouseClient.addNewClient(client as ClientDto).then((res) => {
                    thunkAPI.dispatch(addClientToRedux(res));
                    const newProj = new ProjectDto({ ...project, clientId: res.id });

                    return WarehouseClient.addNewProject(newProj).then((p) => {
                        const newSite = new SiteDto({ ...site, projectId: p.id });

                        return WarehouseClient.addNewSite(p.id ?? 0, newSite);
                    });
                });
                savedSite = await addSitePromise;
            } else {
                const updateSitePromise = WarehouseClient.addNewClient(client as ClientDto).then((res) => {
                    thunkAPI.dispatch(addClientToRedux(res));
                    const newProj = new ProjectDto({ ...project, clientId: res.id });

                    return WarehouseClient.addNewProject(newProj).then((p) => {
                        const newSite = new SiteDto({ ...site, projectId: p.id });

                        return WarehouseClient.updateSite(siteId, newSite);
                    });
                });
                savedSite = await updateSitePromise;
            }
        } else if ((!projectId || projectId === 0) && isNewProject && !!clientId && clientId !== 0) {
            const newProj = new ProjectDto({ ...project, clientId: clientId });
            if (!siteId || siteId === 0) {
                const addSitePromise = WarehouseClient.addNewProject(newProj).then((p) => {
                    const newSite = new SiteDto({ ...site, projectId: p.id });

                    return WarehouseClient.addNewSite(p.id ?? 0, newSite);
                });

                savedSite = await addSitePromise;
            } else {
                const updateSitePromise = WarehouseClient.addNewProject(newProj).then((p) => {
                    const newSite = new SiteDto({ ...site, projectId: p.id });

                    return WarehouseClient.updateSite(siteId, newSite);
                });

                savedSite = await updateSitePromise;
            }
        } else {
            if (!siteId || siteId === 0) {
                const addSitePromise = WarehouseClient.addNewSite(projectId ?? 0, new SiteDto(site));

                savedSite = await addSitePromise;
            } else {
                const addSitePromise = WarehouseClient.updateSite(siteId, new SiteDto(site));

                savedSite = await addSitePromise;
            }
        }
    }

    return savedSite;
});

export const importClientsForSiteForm = createAsyncThunk<ClientDto[]>(
    'site/importClientsForSiteForm',
    async (payload, thunkAPI) => {
        return WarehouseClient.getAllClients(false);
    },
);

export const importProjectsForSiteForm = createAsyncThunk<ProjectDto[], number>(
    'site/importProjectsForSiteForm',
    async (payload, thunkAPI) => {
        return WarehouseClient.getAllProjectsForClient(payload, false, false, false);
    },
);

export const importSitesForProjectForSiteForm = createAsyncThunk<SiteDto[], number>(
    'site/importSitesForProjectForSiteForm',
    async (payload, thunkAPI) => {
        return WarehouseClient.getAllSitesForProject(payload, false);
    },
);

// Sites come without their goods: the same units are already loaded by importAllGoods, and
// components that need per-site stock combine the two through useSitesWithGoods.
export const importSites = createAsyncThunk<SiteDto[]>('site/importSites', async (payload, thunkAPI) => {
    return WarehouseClient.getAllSites(false, true);
});

export const importSiteForSiteForm = createAsyncThunk<SiteDto, number>(
    'site/importSiteForSiteForm',
    async (payload, thunkAPI) => {
        return WarehouseClient.getSiteById(payload, true, true);
    },
);

export const importClientForSiteForm = createAsyncThunk<SiteDto, number>(
    'site/importClientForSiteForm',
    async (payload, thunkAPI) => {
        return WarehouseClient.getClientById(payload, false);
    },
);
